import { useNavigate } from 'react-router-dom';
import { Button } from 'react-bootstrap';
import { PERMISSIONS } from '../constants/permissions';
import './AccessDenied.css';

/** Friendly section names for route-level permission checks */
export const SECTION_BY_PERMISSION = {
  [PERMISSIONS.DASHBOARD_NAVIGATE]: 'the dashboard',
  [PERMISSIONS.SETTINGS_READ]: 'Branding',
  [PERMISSIONS.SETTINGS_UPDATE]: 'Branding',
  [PERMISSIONS.STAFF_DEMAND_REPORT_READ]: 'Staff Demand Report',
  [PERMISSIONS.STAFF_PRICING_READ]: 'Staff Prices',
  [PERMISSIONS.STAFF_PRICING_UPDATE]: 'Staff Prices',
  [PERMISSIONS.PRICE_ITEM_READ]: 'Price Items',
  [PERMISSIONS.PRICE_ITEM_CREATE]: 'Price Items',
  [PERMISSIONS.PRICE_ITEM_UPDATE]: 'Price Items',
  [PERMISSIONS.PRICE_ITEM_DELETE]: 'Price Items',
  [PERMISSIONS.BILLING_READ]: 'Billing',
  [PERMISSIONS.BILLING_MANAGE]: 'Billing',
  [PERMISSIONS.BILLING_CONFIG_MANAGE]: 'Billing Setup',
  [PERMISSIONS.BILLING_SHEET_CREATE]: 'Billing Sheets',
  [PERMISSIONS.BILLING_SHEET_UPDATE]: 'Billing Sheets',
  [PERMISSIONS.BILLING_SHEET_DELETE]: 'Billing Sheets',
  [PERMISSIONS.BILLING_WORKSHEET_CREATE]: 'Billing Worksheets',
  [PERMISSIONS.BILLING_WORKSHEET_UPDATE]: 'Billing Worksheets',
  [PERMISSIONS.BILLING_WORKSHEET_ARCHIVE]: 'Billing Worksheets',
  [PERMISSIONS.BILLING_WORKSHEET_REMOVE]: 'Billing Worksheets',
  [PERMISSIONS.BILLING_WORKSHEET_DELETE]: 'Billing Worksheets',
  [PERMISSIONS.ACTIVITY_READ]: 'Activities',
  [PERMISSIONS.ACTIVITY_UPDATE]: 'Activities',
  [PERMISSIONS.ACTIVITY_DELETE]: 'Activities',
  [PERMISSIONS.QUOTATION_READ]: 'Quotations',
  [PERMISSIONS.QUOTATION_CREATE]: 'Quotations',
  [PERMISSIONS.QUOTATION_UPDATE]: 'Quotations',
  [PERMISSIONS.SERVICE_PLAN_READ]: 'Service Plans',
  [PERMISSIONS.SERVICE_PLAN_CREATE]: 'Service Plans',
  [PERMISSIONS.SERVICE_PLAN_UPDATE]: 'Service Plans',
  [PERMISSIONS.ASSESSMENT_READ]: 'Health Assessments',
  [PERMISSIONS.ASSESSMENT_CREATE]: 'Health Assessments',
  [PERMISSIONS.CLIENT_READ]: 'Service Recipients',
  [PERMISSIONS.CLIENT_CREATE]: 'Service Recipients',
  [PERMISSIONS.CLIENT_UPDATE]: 'Service Recipients',
};

export function getAccessDeniedMessage({ permission, role, section, message } = {}) {
  if (message) return message;

  const sectionLabel = section
    || (permission ? SECTION_BY_PERMISSION[permission] : null)
    || null;

  if (role) {
    return sectionLabel
      ? `You need ${role} access to open ${sectionLabel}. Ask your manager if you think this is a mistake.`
      : `You need ${role} access to open this area. Ask your manager if you think this is a mistake.`;
  }

  if (sectionLabel) {
    return `You don't currently have access to ${sectionLabel}. Ask your manager if you need this section enabled for your role.`;
  }

  return "You don't currently have access to this area. Ask your manager if you need it enabled for your role.";
}

const AccessDenied = ({
  permission,
  role,
  section,
  message,
  showPermission = false,
  onGoHome,
  onGoBack,
  primaryLabel,
  hideGoBack = false,
}) => {
  const navigate = useNavigate();
  const friendlyMessage = getAccessDeniedMessage({ permission, role, section, message });
  const headingSection = section || (permission ? SECTION_BY_PERMISSION[permission] : null);

  const handleGoHome = () => {
    if (onGoHome) {
      onGoHome();
    } else {
      navigate('/home');
    }
  };

  const handleGoBack = () => {
    if (onGoBack) {
      onGoBack();
    } else {
      navigate(-1);
    }
  };

  return (
    <div className="access-denied-container">
      <div className="access-denied-content" role="status" aria-live="polite">
        <div className="access-denied-icon" aria-hidden="true">
          <svg
            width="72"
            height="72"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <rect x="5" y="11" width="14" height="10" rx="2" />
            <path d="M8 11V8a4 4 0 0 1 8 0v3" />
            <circle cx="12" cy="16" r="1" fill="currentColor" stroke="none" />
          </svg>
        </div>

        <p className="access-denied-eyebrow">Access limited</p>
        <h1 className="access-denied-title">
          {headingSection ? `Can't open ${headingSection}` : "You can't open this page"}
        </h1>

        <p className="access-denied-message">{friendlyMessage}</p>

        {showPermission && permission && (
          <div className="access-denied-permission">
            <span className="permission-label">For admins</span>
            <code className="permission-code">{permission}</code>
          </div>
        )}

        <div className="access-denied-actions">
          {!hideGoBack && (
            <Button variant="outline-secondary" onClick={handleGoBack}>
              Go back
            </Button>
          )}
          <Button className="access-denied-primary" onClick={handleGoHome}>
            {primaryLabel || 'Back to home'}
          </Button>
        </div>

        <p className="access-denied-help">
          Already supposed to have access? Try switching tenants, or contact your manager.
        </p>
      </div>
    </div>
  );
};

export default AccessDenied;
